<template>
  <div class="line-integration">
    <!-- 簡潔なメッセージ -->
    <div class="message-section">
      <p class="integration-message">
        📱 公式ラインに連携コードを送って、LINE連携してください！
      </p>
    </div>

    <!-- 連携状態を取得できなかったとき（コードは発行し直さない。読み込みだけやり直す） -->
    <div v-if="linkCodesLoadError" class="link-codes-load-error" role="alert">
      <p>LINE連携の状態を読み込めませんでした。通信状態を確認して、もう一度お試しください。</p>
      <button type="button" class="btn-update-status" @click="loadLinkCodes">もう一度読み込む</button>
    </div>

    <!-- 未連携時の案内バナー -->
    <div v-if="showUpdateGuide" class="connection-guide">
      <div class="guide-content">
        <div class="guide-icon">📱</div>
        <div class="guide-text">
          <h3>LINE連携完了後のお手続き</h3>
          <p>連携コードをLINEに送信後、下のボタンを押して連携状態を確認してください。</p>
        </div>
        <button @click="checkConnectionStatus" class="btn-update-status">
          🔄 連携状態を更新
        </button>
      </div>
    </div>

    <!-- マイページでは未成年（保護者＋学習者）フローに統一 -->

    <!-- 学習者LINE連携セクション（非表示：子ども本人のLINE連携は現在案内していない） -->
    <div v-if="false" class="integration-section">
      <h3 class="section-title">🎓 学習者のLINE連携（任意）</h3>
      
      <div class="link-code-container">
        <div class="code-display">
          <span class="code-label">連携コード:</span>
          <span class="code-value">{{ childLinkCode || '生成中...' }}</span>
        </div>
        
        <div class="code-actions">
          <button @click="copyLinkCode(childLinkCode)" class="btn-copy">
            📋 コピー
          </button>
          <button @click="shareLinkCode(childLinkCode, 'child')" class="btn-share">
            📤 シェア
          </button>
        </div>
      </div>
      
      <div class="friend-add-methods">
        <p class="instructions">友達追加方法を選択:</p>
        <div class="method-buttons">
          <button @click="addFriendByTap" class="btn-method">
            📱 タップで追加
          </button>
          <button @click="shareOfficialLine" class="btn-method">
            📤 公式ラインをシェア
          </button>
        </div>
      </div>
      
      <div class="status-display" v-if="childStatus">
        <span class="status-label">状態:</span>
        <span :class="['status-value', childStatus.connected ? 'connected' : 'disconnected']">
          {{ childStatus.connected ? '✅ 連携済み' : '❌ 未連携' }}
        </span>
      </div>

      <div v-if="childStatus?.connected" class="parent-list">
        <div class="parent-item">
          <span class="parent-name">{{ childLineDisplayLabel }}</span>
          <button type="button" class="btn-unlink" @click="unlinkLine('child')">解除</button>
        </div>
      </div>
      
      <!-- 学習者個別の案内 -->
      <div v-if="showChildUpdateGuide" class="individual-guide">
        <div class="guide-content">
          <div class="guide-icon">🎓</div>
          <div class="guide-text">
            <p>学習者のLINE連携コードを送信後、下のボタンを押してください。</p>
          </div>
          <button @click="checkConnectionStatus" class="btn-update-individual">
            🔄 学習者の状態を更新
          </button>
        </div>
      </div>
    </div>

    <!-- 保護者LINE連携セクション -->
    <div class="integration-section">
      <h3 class="section-title">👨‍👩‍👧‍👦 保護者のLINE連携（必須）</h3>

      <div class="status-display" v-if="parentStatus">
        <span class="status-label">連携人数:</span>
        <span class="status-value connected">{{ parentStatus.count ?? 0 }}人</span>
      </div>

      <div v-if="parentIntegrations.length" class="parent-list">
        <div v-for="p in parentIntegrations" :key="p.id || p.lineUserId" class="parent-item">
          <span class="parent-name">{{ p.displayName || '保護者' }}</span>
          <button class="btn-unlink" @click="unlinkParent(p.id, p.lineUserId)">解除</button>
        </div>
      </div>
      
      <div v-if="parentLinkCode" class="link-code-container">
        <div class="code-display">
          <span class="code-label">連携コード:</span>
          <span class="code-value">{{ parentLinkCode }}</span>
        </div>
        
        <div class="code-actions">
          <button @click="copyLinkCode(parentLinkCode)" class="btn-copy">
            📋 コピー
          </button>
          <button @click="shareLinkCode(parentLinkCode, 'parent')" class="btn-share">
            📤 シェア
          </button>
        </div>
      </div>

      <div v-else class="link-code-container">
        <div class="code-display">
          <span class="code-label">連携コード:</span>
          <span class="code-value">（未表示）</span>
        </div>
        <p class="instructions" style="margin: 8px 0 0 0;">
          2人目以降の保護者を連携する場合は「➕ 保護者を追加」で新しいコードを発行してください。
        </p>
      </div>

      <div class="code-actions" style="margin-bottom: 16px;">
        <button @click="generateParentLinkCode" class="btn-share">
          ➕ 保護者を追加
        </button>
      </div>
      
      <div class="friend-add-methods">
        <div class="method-buttons">
          <button @click="addFriendByTap" class="btn-method">
            📱 タップで追加
          </button>
          <button @click="shareOfficialLine" class="btn-method">
            📤 公式ラインをシェア
          </button>
        </div>
      </div>
      
      <!-- 親は複数連携のため、解除は一覧から個別に行います -->
      
      <!-- 保護者個別の案内 -->
      <div v-if="showParentUpdateGuide" class="individual-guide">
        <div class="guide-content">
          <div class="guide-icon">👨‍👩‍👧‍👦</div>
          <div class="guide-text">
            <p>保護者のLINE連携コードを送信後、下のボタンを押してください。</p>
          </div>
          <button @click="checkConnectionStatus" class="btn-update-individual">
            🔄 保護者の状態を更新
          </button>
        </div>
      </div>
    </div>


  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { supabase } from '../supabaseClient'
import { decideLinkCodesLoad } from '../utils/lineLinkCodesLoad.js'

const props = defineProps({
  learnerId: { type: String, required: false, default: null }
})

// リアクティブ変数
const parentLinkCode = ref('')
const childLinkCode = ref('')
const selfLinkCode = ref('')
const parentStatus = ref(null)
const parentIntegrations = ref([])
const childStatus = ref(null)
const selfStatus = ref(null)
const showParentUpdateGuide = ref(false) // 保護者未連携時の案内表示
const showChildUpdateGuide = ref(false) // 学習者未連携時の案内表示

// 学習者・保護者のどちらか未連携なら案内バナーを出す
const showUpdateGuide = computed(() => {
  const p = parentStatus.value
  const c = childStatus.value
  const parentNeed = p && !p.connected
  const childNeed = c && !c.connected
  return !!(parentNeed || childNeed)
})

const childLineDisplayLabel = computed(() => {
  const raw = (childStatus.value?.displayName || '').trim()
  if (raw && raw !== 'Unknown') return raw
  return 'LINE連携済み（表示名は未取得です）'
})

// 連携コード生成
const generateLinkCode = (userId, type) => {
  const prefix = type === 'parent' ? 'P' : type === 'child' ? 'C' : 'S'
  const userIdSuffix = userId.slice(-4)
  const randomCode = Math.random().toString(36).substring(2, 8).toUpperCase()
  return `${prefix}${userIdSuffix}${randomCode}`
}

// 連携コードの読み込み（超シンプル版）
// 連携状態の読み込みに失敗したか（失敗時はコードを発行し直さず、保存済みのコード・表示も上書きしない。指示書 A-22）
const linkCodesLoadError = ref(false)

const loadLinkCodes = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) return

    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
    const session = await supabase.auth.getSession()
    const accessToken = session.data.session?.access_token

    // 1. 既存の連携コードを取得（child は learner_id ごと）
    const url = props.learnerId
      ? `${apiBaseUrl}/api/line-link-codes/${user.id}?learner_id=${encodeURIComponent(props.learnerId)}`
      : `${apiBaseUrl}/api/line-link-codes/${user.id}`
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    })
    const result = response.ok ? await response.json().catch(() => null) : null
    const decision = decideLinkCodesLoad({ ok: response.ok, json: result, learnerId: props.learnerId })

    if (decision.kind === 'error') {
      // 取得失敗は「コードが無い・未連携」ではない。発行（既存コードの差し替え）はせず、読み込みのやり直しだけを案内する
      console.warn('⚠️ 連携コードの取得に失敗しました（再発行はしません）:', response.status)
      linkCodesLoadError.value = true
      return
    }

    linkCodesLoadError.value = false
    parentLinkCode.value = result.data.parentCode
    childLinkCode.value = result.data.childCode
    selfLinkCode.value = result.data.selfCode
    parentStatus.value = result.data.parentStatus
    parentIntegrations.value = result.data.parentIntegrations || []
    childStatus.value = result.data.childStatus
    selfStatus.value = result.data.selfStatus

    // 正常に取得できて、コードが無いと確認できたときだけ発行する
    if (decision.generate === 'child') {
      console.log('🆕 child の連携コードが不足しています（learner 単位）。childのみ新規生成します')
      await generateChildLinkCode()
    } else if (decision.generate === 'all') {
      console.log('🆕 連携コードが不足しています。新規生成します')
      await generateNewLinkCodes()
    }
  } catch (error) {
    // 通信例外も「コードが無い」ではない。発行せず、読み込みのやり直しを案内する
    console.error('❌ 連携コード読み込みエラー（再発行はしません）:', error)
    linkCodesLoadError.value = true
  }
}

// 親を追加するための新しい連携コードを1つ発行（既存の親連携は維持）
const generateParentLinkCode = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) throw new Error('ユーザーが認証されていません')

    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
    const session = await supabase.auth.getSession()
    const accessToken = session.data.session?.access_token

    const response = await fetch(`${apiBaseUrl}/api/line-parent-codes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }
    })

    const result = await response.json().catch(() => ({}))
    if (!response.ok || !result.success) {
      throw new Error(result.error || `連携コード生成に失敗しました: ${response.status}`)
    }
    parentLinkCode.value = result.data.code
    // 親の状態は維持（一覧で管理）
    alert('保護者追加用の連携コードを発行しました。公式LINEに送って連携してください。')
  } catch (e) {
    console.error('generateParentLinkCode error:', e)
    alert(`保護者の連携コード生成に失敗しました: ${e.message}`)
  }
}

// child のみ新しい連携コードを生成（learner 単位）
const generateChildLinkCode = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) throw new Error('ユーザーが認証されていません')
    if (!props.learnerId) throw new Error('learnerId が必要です')

    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
    const newChildCode = generateLinkCode(user.id, 'child')
    const requestBody = { childCode: newChildCode, learner_id: props.learnerId }

    const session = await supabase.auth.getSession()
    const accessToken = session.data.session?.access_token

    const response = await fetch(`${apiBaseUrl}/api/line-link-codes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('❌ child 連携コード生成エラー:', errorText)
      throw new Error(`child 連携コード生成に失敗しました: ${response.status}`)
    }

    childLinkCode.value = newChildCode
    childStatus.value = { connected: false }
    console.log('✅ child 連携コード生成完了（learner単位）')
  } catch (error) {
    console.error('❌ child 連携コード生成エラー:', error)
    alert(`学習者の連携コード生成に失敗しました: ${error.message}`)
  }
}

// 新しい連携コードを生成
const generateNewLinkCodes = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) throw new Error('ユーザーが認証されていません')
    
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
    
    // 両方のタイプの連携コードを生成
    const newParentCode = generateLinkCode(user.id, 'parent')
    const newChildCode = generateLinkCode(user.id, 'child')
    
    const requestBody = {
      parentCode: newParentCode,
      childCode: newChildCode
    }
    
    // バックエンドに保存
    const session = await supabase.auth.getSession()
    const accessToken = session.data.session?.access_token
    
    const response = await fetch(`${apiBaseUrl}/api/line-link-codes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(requestBody)
    })
    
    if (response.ok) {
      // 両方のタイプのコードを設定
      parentLinkCode.value = requestBody.parentCode
      childLinkCode.value = requestBody.childCode
      
      // 初期状態を設定
      parentStatus.value = { connected: false }
      childStatus.value = { connected: false }
      
      console.log('✅ 連携コード生成完了（両方タイプ）')
    } else {
      const errorText = await response.text()
      console.error('❌ 連携コード生成エラー:', errorText)
      throw new Error(`連携コード生成に失敗しました: ${response.status}`)
    }
    
  } catch (error) {
    console.error('❌ 連携コード生成エラー:', error)
    alert(`連携コードの生成に失敗しました: ${error.message}`)
  }
}

// 連携コードをクリップボードにコピー
const copyLinkCode = async (code) => {
  try {
    await navigator.clipboard.writeText(code)
    alert('連携コードをコピーしました。公式ラインに張り付けてください')
  } catch (error) {
    console.error('コピーエラー:', error)
    // フォールバック
    const textArea = document.createElement('textarea')
    textArea.value = code
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    alert('連携コードをコピーしました。公式ラインに張り付けてください')
  }
}

// 連携コードをシェア
const shareLinkCode = (code, type) => {
  const typeText = type === 'parent' ? '親' : type === 'child' ? '子' : '本人'
  const message = `オトエイゴ ${typeText}LINE連携コード: ${code}\n\n手順:\n1. オトエイゴ公式LINEを友達追加\n2. このコードをLINEで送信してください`
  
  if (navigator.share) {
    navigator.share({
      title: `オトエイゴ ${typeText}LINE連携`,
      text: message
    }).catch(error => {
      console.log('シェアがキャンセルされました:', error)
    })
  } else {
    // フォールバック: クリップボードにコピー
    copyLinkCode(message)
  }
}

// LINE公式アカウント友達追加 (タップ)
const addFriendByTap = () => {
  const officialId = import.meta.env.VITE_LINE_OFFICIAL_ID || 'otoeigo'
  const addFriendUrl = `https://line.me/R/ti/p/@${officialId}`
  window.open(addFriendUrl, '_blank')
}

// 公式LINEをシェア
const shareOfficialLine = () => {
  const officialId = import.meta.env.VITE_LINE_OFFICIAL_ID || 'otoeigo'
  const addFriendUrl = `https://line.me/R/ti/p/@${officialId}`
  const shareText = `オトエイゴ公式LINEアカウント\n${addFriendUrl}`
  
  if (navigator.share) {
    navigator.share({
      title: 'オトエイゴ公式LINE',
      text: shareText,
      url: addFriendUrl
    }).catch(err => {
      console.log('シェア失敗:', err)
      copyToClipboard(addFriendUrl)
    })
  } else {
    copyToClipboard(addFriendUrl)
  }
}

// クリップボードにコピー（フォールバック）
const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    alert('公式LINE URLをコピーしました！')
  }).catch(() => {
    alert('コピーに失敗しました。手動でコピーしてください。')
  })
}

// LINE連携解除
const unlinkLine = async (type) => {
  const typeText = type === 'parent' ? '保護者' : type === 'child' ? '学習者' : '本人'
  if (!confirm(`${typeText}のLINE連携を解除しますか？`)) {
    return
  }
  
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) throw new Error('ユーザーが認証されていません')
    
    const relationshipType = type === 'self' ? 'learner' : type === 'child' ? 'learner' : type
    
    console.log('🔍 連携解除デバッグ:', { userId: user.id, type, relationshipType })
    
    // 1. line_integrationsから削除（学習者は API が返した integrationId があれば主キーで1行だけ確実に削除）
    let integrationError = null
    if (type === 'child' && childStatus.value?.integrationId) {
      const { error } = await supabase
        .from('line_integrations')
        .delete()
        .eq('id', childStatus.value.integrationId)
        .eq('user_id', user.id)
      integrationError = error
    } else {
      let integrationQuery = supabase
        .from('line_integrations')
        .delete()
        .eq('user_id', user.id)
        .eq('relationship_type', relationshipType)
      if (type === 'child' && props.learnerId) {
        integrationQuery = integrationQuery.eq('learner_id', props.learnerId)
      } else {
        integrationQuery = integrationQuery.is('learner_id', null)
      }
      const res = await integrationQuery
      integrationError = res.error
    }
    
    if (integrationError) throw integrationError
    console.log('✅ line_integrations削除完了')
    
    // 2. line_link_codesのline_user_idも削除（連携コードを再利用可能にする）
    const childLineUid = type === 'child' ? (childStatus.value?.lineUserId || null) : null
    let linkCodeError = null
    if (type === 'child' && childLineUid) {
      const res = await supabase
        .from('line_link_codes')
        .update({ line_user_id: null, used_at: null })
        .eq('user_id', user.id)
        .eq('type', 'child')
        .eq('line_user_id', childLineUid)
      linkCodeError = res.error
    } else {
      let linkCodeQuery = supabase
        .from('line_link_codes')
        .update({ line_user_id: null, used_at: null })
        .eq('user_id', user.id)
        .eq('type', type)
      if (type === 'child' && props.learnerId) {
        linkCodeQuery = linkCodeQuery.eq('learner_id', props.learnerId)
      } else {
        linkCodeQuery = linkCodeQuery.is('learner_id', null)
      }
      const res = await linkCodeQuery
      linkCodeError = res.error
    }
    
    console.log('📊 line_link_codes更新結果:', { linkCodeError })
    
    if (linkCodeError) {
      console.error('❌ line_link_codesの更新に失敗:', linkCodeError)
      alert(`連携解除は完了しましたが、コードの再利用設定に失敗しました: ${linkCodeError.message}`)
    } else {
      console.log('✅ line_link_codesの更新成功')
    }
    
    // 状態を更新
    if (type === 'self') {
      selfStatus.value = { connected: false }
    } else if (type === 'child') {
      childStatus.value = { connected: false, displayName: null, integrationId: null, lineUserId: null }
    } else if (type === 'parent') {
      parentStatus.value = { connected: false, count: 0 }
      parentIntegrations.value = []
    }
    
    alert('LINE連携を解除しました。連携コードは再利用可能になりました。')
    
  } catch (error) {
    console.error('LINE連携解除エラー:', error)
    alert('LINE連携解除に失敗しました')
  }
}

// 親（複数）: 特定の親だけ解除
const unlinkParent = async (integrationId, lineUserId) => {
  if (!confirm('この保護者のLINE連携を解除しますか？')) return
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) throw new Error('ユーザーが認証されていません')

    // 同じ line_user_id が複数行ある運用に対応するため、基本は integration の主キー(id)で1行だけ削除する
    let deleteQuery = supabase
      .from('line_integrations')
      .delete()
      .eq('user_id', user.id)
      .eq('relationship_type', 'parent')
      .is('learner_id', null)

    if (integrationId) {
      deleteQuery = deleteQuery.eq('id', integrationId)
    } else {
      // 念のため旧データ互換（id が無い場合のみ line_user_id で削除）
      deleteQuery = deleteQuery.eq('line_user_id', lineUserId)
    }

    const { error } = await deleteQuery

    if (error) throw error
    await loadLinkCodes()
    updateGuideVisibility()
  } catch (e) {
    console.error('unlinkParent error:', e)
    alert('解除に失敗しました')
  }
}

// 連携状態を定期的にチェック
const checkConnectionStatus = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) return
    
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
    const session = await supabase.auth.getSession()
    const accessToken = session.data.session?.access_token
    
    const url = props.learnerId
      ? `${apiBaseUrl}/api/line-link-codes/${user.id}?learner_id=${encodeURIComponent(props.learnerId)}`
      : `${apiBaseUrl}/api/line-link-codes/${user.id}`
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    })
    
    if (response.ok) {
      const result = await response.json()
      
      if (result.success && result.data) {
        // 状態・コード・保護者一覧を loadLinkCodes と同様に同期（更新ボタンで一覧が空のままになる不具合を防ぐ）
        parentStatus.value = result.data.parentStatus
        parentIntegrations.value = result.data.parentIntegrations || []
        childStatus.value = result.data.childStatus
        selfStatus.value = result.data.selfStatus
        if (result.data.parentCode !== undefined) parentLinkCode.value = result.data.parentCode
        if (result.data.childCode !== undefined) childLinkCode.value = result.data.childCode
        if (result.data.selfCode !== undefined) selfLinkCode.value = result.data.selfCode

        updateGuideVisibility()
      }
    }
  } catch (error) {
    console.error('❌ 連携状態チェックエラー:', error)
  }
}

// 個別の案内表示判定
const updateGuideVisibility = () => {
  showParentUpdateGuide.value = !parentStatus.value?.connected
  showChildUpdateGuide.value = !childStatus.value?.connected
}


// コンポーネント初期化
onMounted(async () => {
  await loadLinkCodes()
  
  // 初期状態で案内表示を判定
  updateGuideVisibility()
  
  // 5秒ごとに連携状態をチェック（最初の30秒間）
  const checkInterval = setInterval(checkConnectionStatus, 5000)
  setTimeout(() => {
    clearInterval(checkInterval)
  }, 30000)
})

// タブ切り替え（learnerId 変更）で子のコード・状態を差し替え
watch(() => props.learnerId, async () => {
  await loadLinkCodes()
  updateGuideVisibility()
})
</script>

<style scoped>
.link-codes-load-error {
  margin: 0 0 16px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
}
.link-codes-load-error p {
  margin: 0 0 8px;
}

.line-integration {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.integration-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
}

.section-title {
  margin: 0 0 16px 0;
  color: #495057;
  font-size: 18px;
  font-weight: 600;
}

.link-code-container {
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #dee2e6;
}

.code-display {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.code-label {
  font-weight: 500;
  color: #6c757d;
  min-width: 80px;
}

.code-value {
  font-family: 'Consolas', 'Monaco', 'Lucida Console', 'Liberation Mono', 'DejaVu Sans Mono', 'Bitstream Vera Sans Mono', 'Courier New', monospace;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  background: #f8f9fa;
  letter-spacing: 0.5px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.code-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-copy, .btn-share {
  padding: 6px 12px;
  border: 1px solid #007bff;
  background: white;
  color: #007bff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-copy:hover, .btn-share:hover {
  background: #007bff;
  color: white;
}

.friend-add-methods {
  margin-bottom: 16px;
}

.instructions {
  margin: 0 0 8px 0;
  color: #6c757d;
  font-size: 14px;
}

.method-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-method {
  padding: 10px 16px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.btn-method:hover {
  background: #218838;
}

.status-display {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.status-label {
  font-weight: 500;
  color: #6c757d;
}

.status-value.connected {
  color: #28a745;
  font-weight: 600;
}

.status-value.disconnected {
  color: #dc3545;
  font-weight: 600;
}

.parent-list {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.parent-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}

.parent-name {
  font-weight: 600;
  color: #495057;
}

.btn-unlink {
  padding: 4px 8px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-left: 8px;
  transition: background-color 0.2s;
}

.btn-unlink:hover {
  background: #c82333;
}

.message-section {
  background: #e3f2fd;
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid #2196f3;
  text-align: center;
}

.integration-message {
  margin: 0;
  color: #1976d2;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
}

/* 案内バナーのスタイル */
.connection-guide {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  border: 2px solid #bbdefb;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15);
}

.guide-content {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.guide-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.guide-text {
  flex: 1;
  min-width: 200px;
}

.guide-text h3 {
  margin: 0 0 8px 0;
  color: #1976d2;
  font-size: 16px;
  font-weight: 600;
}

.guide-text p {
  margin: 0;
  color: #424242;
  font-size: 14px;
  line-height: 1.4;
}

.btn-update-status {
  padding: 12px 20px;
  background: #1976d2;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.btn-update-status:hover {
  background: #1565c0;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
}

.btn-update-status:active {
  transform: translateY(0);
}

/* 個別案内のスタイル */
.individual-guide {
  background: linear-gradient(135deg, #fff3e0 0%, #fce4ec 100%);
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  border: 1px solid #ffcc02;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.15);
}

.individual-guide .guide-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.individual-guide .guide-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.individual-guide .guide-text {
  flex: 1;
  min-width: 150px;
}

.individual-guide .guide-text p {
  margin: 0;
  color: #e65100;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
}

.btn-update-individual {
  padding: 8px 16px;
  background: #ff9800;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(255, 152, 0, 0.3);
}

.btn-update-individual:hover {
  background: #f57c00;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(255, 152, 0, 0.4);
}

.btn-update-individual:active {
  transform: translateY(0);
}



/* レスポンシブ対応 */
@media (max-width: 600px) {
  .line-integration {
    padding: 15px;
  }
  
  .integration-section {
    padding: 15px;
  }
  
  .code-display {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .code-value {
    font-size: 16px;
    word-break: break-all;
    letter-spacing: 0.3px;
  }
  
  .method-buttons {
    flex-direction: column;
  }
  
  .btn-method {
    width: 100%;
  }
  
  /* 案内バナーのレスポンシブ対応 */
  .guide-content {
    flex-direction: column;
    text-align: center;
  }
  
  .guide-text {
    min-width: auto;
  }
  
  .btn-update-status {
    width: 100%;
    margin-top: 12px;
  }
  
  /* 個別案内のレスポンシブ対応 */
  .individual-guide .guide-content {
    flex-direction: column;
    text-align: center;
  }
  
  .individual-guide .guide-text {
    min-width: auto;
  }
  
  .btn-update-individual {
    width: 100%;
    margin-top: 8px;
  }
}
</style>
